import React, { useState } from "react"
import { EmailShareButton, WhatsappShareButton } from "react-share"
import { BottomSheet } from "react-spring-bottom-sheet"
import "react-spring-bottom-sheet/dist/style.css"

import { showInfoToast } from "@fuse/sharedComponents/themeToast/toastUtils"
import { letsHangSupportEmail } from "@letshang/constants"

import {
  TDeviceType,
  useDeviceTypeByWidth,
} from "@fuse/customHooks/useDeviceTypeByWidth"

import IconChat from "@fuse/assets/icons/img-icon-chat2.png"
import IconGmail from "@fuse/assets/images/socialMedia/img-gmail.png"
import IconSms from "@fuse/assets/images/socialMedia/img-sms.png"
import IconWhatsapp from "@fuse/assets/images/socialMedia/img-whatsapp.png"

interface GenericFeedbackProps {
  emailRecipient?: string
  emailSubject?: string
  fromScreen?: string
  userId?: string
  showOnDesktop?: boolean
}

const useSupportMailData = ({
  emailRecipient,
  emailSubject,
  fromScreen,
  userId,
}: Pick<
  GenericFeedbackProps,
  "emailRecipient" | "emailSubject" | "fromScreen" | "userId"
>) => {
  const recipient = emailRecipient ?? letsHangSupportEmail
  const subject = emailSubject ?? "Assistance needed: Issue on Letshang app"
  const body = `Hello,\n\nI'm having an issue.\n<Please explain your issue here>\n\nReport details:\nScreen: ${fromScreen ?? "Unknown"}\nTime: ${new Date().toLocaleString()}\nUser ID: ${userId ?? "Not provided"}\n\nRegards`
  const url = `mailto:${recipient}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
  return { url }
}

const SupportShareUI = ({
  url,
  onClose,
}: {
  url: string
  onClose: () => void
}) => {
  const copyToClipboard = () => {
    navigator.clipboard.writeText(letsHangSupportEmail)

    showInfoToast("Email address copied to clipboard!")
    onClose()
  }

  return (
    <div className="p-6 rounded-2xl">
      {/* Header with close button */}
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-white font-medium text-xl">Talk to our team</h3>
        <button
          onClick={onClose}
          className="text-white/70 hover:text-white text-2xl leading-none bg-transparent border-none"
        >
          ×
        </button>
      </div>

      {/* Subtitle - left aligned */}
      <p className="text-white/70 text-sm mb-6 text-left">
        Choose how you'd like to connect with us:
      </p>

      {/* Contact options */}
      <div className="flex flex-col gap-4 mb-8">
        <WhatsappShareButton
          url={url}
          title="Support needed"
          separator=": "
          className="flex items-center gap-4 p-4 rounded-xl bg-white/10 hover:bg-white/20 transition-all duration-200"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-green-500">
            <img src={IconWhatsapp} alt="WhatsApp" className="h-6 w-6" />
          </div>
          <span className="text-white font-medium text-lg">
            Chat on WhatsApp
          </span>
        </WhatsappShareButton>

        <EmailShareButton
          url={url}
          className="flex items-center gap-4 p-4 rounded-xl bg-white/10 hover:bg-white/20 transition-all duration-200"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-500">
            <img src={IconGmail} alt="Gmail" className="h-6 w-6" />
          </div>
          <span className="text-white font-medium text-lg">Email Us</span>
        </EmailShareButton>

        <div
          onClick={copyToClipboard}
          className="flex items-center gap-4  cursor-pointer"
          style={{ minHeight: "64px" }}
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-green-600">
            <img src={IconSms} alt="SMS" className="h-6 w-6" />
          </div>
          <span className="text-white font-medium text-lg">Send SMS</span>
        </div>
      </div>

      {/* Footer text - left aligned */}
      <div className="border-t border-white/20 pt-6">
        <p className="text-white/70 text-sm mb-2 text-left">
          If the links don't work, just email us at:
        </p>
        <div className="flex items-center gap-2 justify-start">
          <span className="text-blue-400 text-sm font-medium">
            {letsHangSupportEmail}
          </span>
          <button
            onClick={() => {
              navigator.clipboard.writeText(letsHangSupportEmail)

              showInfoToast("Email address copied to clipboard!")
            }}
            className="text-white/50 hover:text-white bg-transparent border-none p-0"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
}

const FeedbackTrigger = ({ onClick }: { onClick: () => void }) => (
  <div
    className="text-white/50 flex flex-row gap-2 items-center cursor-pointer"
    onClick={onClick}
  >
    <img src={IconChat} alt="Chat icon" className="h-4 w-4" />
    <p className="sm:text-left text-md text-center">
      Having issues? We can help.
      <span className="underline text-white/50 hover:text-white ml-1">
        Talk to our team.
      </span>
    </p>
  </div>
)

const GenericFeedback: React.FC<GenericFeedbackProps> = (props) => {
  const { showOnDesktop = false } = props
  const deviceType = useDeviceTypeByWidth()
  const [open, setOpen] = useState(false)
  const { url } = useSupportMailData(props)

  const visible =
    (showOnDesktop && deviceType === TDeviceType.DESKTOP) ||
    (!showOnDesktop && deviceType !== TDeviceType.DESKTOP)

  return visible ? (
    <>
      <FeedbackTrigger onClick={() => setOpen(true)} />
      {deviceType === TDeviceType.MOBILE ? (
        <BottomSheet
          open={open}
          onDismiss={() => setOpen(false)}
          className="!bg-transparent"
          style={
            {
              "--rsbs-bg": "transparent",
              "--rsbs-handle-bg": "#999999",
            } as React.CSSProperties
          }
        >
          <SupportShareUI url={url} onClose={() => setOpen(false)} />
        </BottomSheet>
      ) : (
        open && (
          <div
            className="fixed inset-0 bg-black/50 flex justify-center items-center z-50"
            onClick={() => setOpen(false)}
          >
            <div
              className="max-w-md w-full bg-white bg-opacity-20 backdrop-blur-md rounded-2xl p-8"
              onClick={(e) => e.stopPropagation()}
            >
              <SupportShareUI url={url} onClose={() => setOpen(false)} />
            </div>
          </div>
        )
      )}
    </>
  ) : null
}

export const GenericFeedbackCommon: React.FC<GenericFeedbackProps> = (
  props,
) => {
  return <GenericFeedback {...props} showOnDesktop />
}

export default GenericFeedback
